import { Injectable } from '@angular/core';
import { HttpService } from '../../services/http.service';
import { CcImageSearchQuery } from '../../../models/cc-image-search-query';
import { ExternalImageSearchApis } from '../../../config/external-image-search-api.config';
import { IImageWrapper } from '../../../models/image-wrapper';
import { Licenses } from '../../../models/licenses';
import { IStandardizedImageSearchResults } from '../../../models/standardized-image-search-results';
import { getDimensionFromImageSource } from '../../main/main.component';
import { SessionServiceService } from 'src/app/services/session-service.service';

/**
 * Describes a cc image search query
 */
export interface CcImageSearchQueryOptions {
  width?: number;
  height?: number;
  ratio?: number;
  license?: Licenses[];
  page?: number;
}

/**
 * The result of an cc image search query
 */
export interface CcImageSearchResult {
  page_count: number;
  result_count: number;
  results: CcImageSearchImageItem[];
}

/**
 * A result item of the cc image search query
 */
export interface CcImageSearchImageItem {
  title: string;
  id: string;
  creator: string;
  creator_url: string;
  url: string;
  thumbnail: string;
  provider: string;
  source: string;
  license: string;
  license_version: string;
  license_url: string;
  foreign_landing_url: string;
  detail: string;
  fields_matched: string[];
  height?: number;
  width?: number;
}

@Injectable({
  providedIn: 'root'
})
/**
 * Documentation of API:
 * OUTDATED: https://api.creativecommons.engineering/#operation/image_search
 * Since 2021-12-15: https://api.openverse.engineering/v1/#operation/image_search
 */
export class CcImageSearchService {
  constructor(
    private _http: HttpService,
    private _session: SessionServiceService
  ) { }

  /**
   * Query the cc image search api
   * @param query The query
   * @param options Some further options
   */
  public async query(query: string, options?: CcImageSearchQueryOptions): Promise<CcImageSearchResult> {
    return new Promise<CcImageSearchResult>((resolve, reject) => {
      const _queryObject: CcImageSearchQuery = {
        q: query,
        page: (options && options.page) ? options.page : 1
      };

      if (!options || !options.license || options.license.length === 0) {
        _queryObject.license = 'cc0,by,by-sa';
      } else {
        let _convertedLicenses: string = '';
        for (const license of options.license) {
          _convertedLicenses += this.licenseTypeToCcAbbr(license) + ',';
        }
        _convertedLicenses = _convertedLicenses.slice(0, _convertedLicenses.length - 1);
        _queryObject.license = _convertedLicenses;
      }

      const url = ExternalImageSearchApis['cc'].url + '?' + this._http.convertObjectToQueryString(_queryObject);

      console.log('CC URL =', url);
      console.log('CC QUERY OBJECT =', _queryObject);

      this._http.get(url).then(async (apiResult: CcImageSearchResult) => {
        console.log('CC API RESULT =', apiResult);

        let removedResults = false;
        for (const result of apiResult.results) {
          if (!result.height || !result.width) {
            result.height = 200;
            result.width = 200;
            try {
              const dim = await this.retrieveCorrectDimensions(result);
              result.height = dim.height;
              result.width = dim.width;
            } catch (error) {
              removedResults = true;
              apiResult.results.splice(apiResult.results.indexOf(result), 1);
            }
          }
        }

        if (removedResults) {
          this._session.alert(
            $localize`Loading of some images failed.`,
            $localize`There were errors loading at least some of the images.\nPlease try another search source or try again later!`
          );
        }

        resolve(apiResult);
      }).catch((err) => {
        console.log('CC REQUEST FAILED =', err);
        reject('CC API not reachable');
      });
    });
  }

  async retrieveCorrectDimensions(result: CcImageSearchImageItem) {
    return getDimensionFromImageSource(result.url);
  }

  /**
   * Convert the results into the internally used format
   * @param imageItems
   */
  public convertToImageWrapper(imageItems: CcImageSearchImageItem[]): IImageWrapper[] {
    const results: IImageWrapper[] = [];
    for (const imageItem of imageItems) {
      results.push({
        externalId: imageItem.id,
        base64: null,
        license: this.licenseStringToType(imageItem.license),
        licenseVersion: imageItem.license_version,
        licenseUrl: imageItem.license_url,
        description: imageItem.detail,
        title: imageItem.title,
        source: imageItem.url,
        author: imageItem.creator,
        dimension: {
          height: imageItem.height || 200,
          width: imageItem.width || 200,
          ratio: (imageItem.width || 200) / (imageItem.height || 200)
        },
        origin: 'cc'
      });
    }
    return results;
  }

  /**
   * Convert to a standardized result format
   * @param ccImageSearchResult The results
   * @param currentPage The current page
   * @param licenses The used licenses
   */
  public convertToStandardizedApiResults(
    ccImageSearchResult: CcImageSearchResult,
    currentPage: number,
    licenses?: Licenses[]
  ): IStandardizedImageSearchResults {
    return {
      pageCount: ccImageSearchResult.page_count,
      resultCount: ccImageSearchResult.result_count,
      pageCurrent: currentPage,
      pageSize: ccImageSearchResult.results.length,
      license: licenses,
      targetApi: 'cc',
      results: this.convertToImageWrapper(ccImageSearchResult.results)
    };
  }

  /**
   * Convert a license by name and version to the proper enum
   * @param licenseString
   */
  public licenseStringToType(licenseString: string): Licenses {
    switch (licenseString) {
      case 'cc0':
        return Licenses.CC_ZERO;
      case 'by':
        return Licenses.CC_BY;
      case 'by-sa':
        return Licenses.CC_BY_SA;
      default:
        return Licenses.UNKNOWN;
    }
  }

  /**
   * Since cc and the tool used different abbreviations for the licenses, this function converts the enum to the proper cc abbreviation
   * @param license
   */
  public licenseTypeToCcAbbr(license: Licenses): string {
    switch (license) {
      case Licenses.CC_BY:
        return 'by';
      case Licenses.CC_BY_SA:
        return 'by-sa';
      case Licenses.CC_ZERO:
        return 'cc0';
      default:
        throw new Error('License not supported by CC');
    }
  }
}